import type { Modifier } from './modifier';
import type { Constructor } from './types';
import type { ModifierParams } from './modifier.type';
import { ModifierTags, StatusEffect } from './modifier.type';
import type { Player } from './player.type';

export class StatusEffectRegistry {
  private static registry = new Map<StatusEffect, Constructor<Modifier>>();

  static register(type: StatusEffect, constructor: Constructor<Modifier>) {
    this.registry.set(type, constructor);
  }

  static get(type: StatusEffect): Constructor<Modifier> | undefined {
    return this.registry.get(type);
  }

  static create(
    type: StatusEffect,
    owner: Player,
    stacks: number,
    params?: ModifierParams,
  ): Modifier {
    const constructor = this.registry.get(type);
    if (!constructor) {
      throw new Error(`No status effect registered for type: ${type}`);
    }
    const modifier = new constructor();
    modifier.owner = owner;
    modifier.stacks = stacks;
    modifier.type = type;

    if (params) {
      Object.assign(modifier, params);
    }

    if (modifier.duration < 0) {
      modifier.tags.push(ModifierTags.Temporary);
    }

    modifier.init(params);
    return modifier;
  }

  static has(type: string): boolean {
    return this.registry.has(type as StatusEffect);
  }
}

// Decorator for registering status effects
export function RegisterStatusEffect(type: StatusEffect) {
  return function <T extends Constructor<Modifier>>(constructor: T) {
    StatusEffectRegistry.register(type, constructor);
    return constructor;
  };
}

export function listStatusEffects() {
  return Array.from(StatusEffectRegistry['registry'].keys());
}

export function getDescriptions() {
  const descriptions: { [key: string]: string } = {};
  for (const [type, constructor] of StatusEffectRegistry['registry']) {
    const instance = new constructor();
    descriptions[type] = instance.text;
  }
  return descriptions;
}

export function getIcons() {
  const icons: { [key: string]: string } = {};
  for (const [type, constructor] of StatusEffectRegistry['registry']) {
    const instance = new constructor();
    icons[type] = instance.icon;
  }
  return icons;
}
